import { addEventListenerPoly } from "../common/addEventListenerPolyfill"
import { Gender } from "../common/genders"
import { addPageAction } from "../common/newrelic"
import { i18n } from "../common/translation"
import { BaseEntryPoint, exportEntryPoint } from "./base"
import type { IPageContext } from "../cb/interfaces/context"

interface IRegisterContext extends IPageContext {template: string}

exportEntryPoint(class Page extends BaseEntryPoint {
    protected context: IRegisterContext

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protected parseContext(contextObj: Record<string, any>): void {
        super.parseContext(contextObj)
        this.context.template = contextObj["template"]
    }

    protected init(): void {
        super.init()

        this.setupRegister()
    }

    protected setupRegister(): void {
        this.setupGenderSelect()
        this.setupNewRelicEvents()
    }

    protected setupGenderSelect(): void {
        const genderSelect = document.getElementById("id_gender") as HTMLSelectElement
        const subgenderSelect = document.getElementById("id_subgender") as HTMLSelectElement
        const subgenderSpinner = document.getElementById("subgender_spinner") as HTMLElement

        if (genderSelect === null || subgenderSelect === null) {
            return
        }

        if (genderSelect.parentElement !== null) {
            genderSelect.parentElement.appendChild(subgenderSelect)
            genderSelect.parentElement.appendChild(subgenderSpinner)
        }

        const onGenderSelect = () => {
            const text = `${genderSelect.value === "c" ? i18n.sexTextCouple : i18n.sexText}:`
            const label = document.querySelector("label[for='id_gender']")
            if (label !== null) {
                label.textContent = text
            }

            if (genderSelect.value === Gender.Trans || genderSelect.value === Gender.OldTrans) {
                subgenderSelect.style.display = "inline"
            } else {
                subgenderSelect.style.display = "none"
                subgenderSelect.value = ""
            }
        }
        genderSelect.onchange = onGenderSelect

        // Deals with browser's built in form input caching functionality.
        // Want to show the subgender select input if user previously filled gender as trans
        window.addEventListener("pageshow", () => {
            if (genderSelect.value === Gender.Trans || genderSelect.value === Gender.OldTrans) {
                subgenderSelect.style.display = "inline"
            }
        })
    }

    protected setupNewRelicEvents(): void {
        const template = this.context.template
        const formButtonList = this.getFormAndButton(template)
        const form = formButtonList[0] as HTMLFormElement
        const submitButton = formButtonList[1] as HTMLElement

        if (form === undefined || form === null || submitButton === undefined || submitButton === null) {
            return
        }

        // SignupStarted NR event
        addPageAction("SignupStarted", { "template": template })

        // SignupFieldChanged NR event
        let formStarted = false
        function markInteraction(e: Event): void {
            if (formStarted) {
                return
            }
            formStarted = true
            addPageAction("SignupFieldChanged", { "template": template, "input": (e.target as HTMLInputElement).name })
        }

        for (const input of form.querySelectorAll("input")) {
            if (input.type === "hidden" || input.type === "submit") {
                continue
            }
            // For IE checkboxes
            if (input.type === "checkbox") {
                addEventListenerPoly("change", input as HTMLElement, markInteraction)
            }
            addEventListenerPoly("input", input as HTMLElement, markInteraction)
        }
        for (const input of form.querySelectorAll("select")) {
            addEventListenerPoly("change", input as HTMLElement, markInteraction)
        }
    }

    private getFormAndButton(template: string): [Element?, Element?] {
        let form: Element | undefined
        let submitButton: Element | undefined

        const determineFormAndButton = (formSelector: string, buttonSelector: string) => {
            const queriedForm = document.querySelector(formSelector)
            form = queriedForm !== null ? queriedForm : undefined
            if (form !== undefined) {
                const queriedButton = form.querySelector(buttonSelector)
                submitButton = queriedButton !== null ? queriedButton : undefined
            }
        }

        if (template === "register") {
            determineFormAndButton("form[id='id_form_register']", "input[type='submit']")
        } else if (template === "register_mobile") {
            determineFormAndButton("form[id='id_form_register']", "button[type='submit']")
        } else if (template === "register_iframe") {
            determineFormAndButton(".allfree-iframe-body form", "input[type='submit']")
        } else if (template === "affiliate_register") {
            determineFormAndButton("#main form", "input[type='submit']")
        } else if (template === "sso_register") {
            determineFormAndButton("form[id='id_sso_complete']", "input[type='submit']")
        } else if (template === "sso_register_mobile") {
            determineFormAndButton("form[id='id_sso_complete']", "button[type='submit']")
        } else {
            form = undefined
            submitButton = undefined
        }

        return [form, submitButton]
    }
})
